import React, { useState, useEffect } from 'react';
import {
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Box,
    Paper,
    Button
} from '@material-ui/core';
import { withStyles } from "@material-ui/core/styles";
import AddIcon from '@material-ui/icons/Add';
import { CatalogHierarchyService } from '../../../shared/service/catalogHierarchyService';

const styles = theme => ({
    container: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(2),
        padding: "1rem 0",
    },
    formControl: {
        width: "100%",
    },
    actionContainer: {
        display: "flex",
        justifyContent: "flex-end",
        marginTop: theme.spacing(1)
    }
});

const QuotePageEquipmentSearch = ({
    onSelectEquipment,
    classes = {},
    currentLanguage
}) => {
    const sharedLanguage = currentLanguage.bepiService.shared;

    const [categorias, setCategorias] = useState([]);
    const [produtos, setProdutos] = useState([]);
    const [servicos, setServicos] = useState([]);
    
    const [loadingCategorias, setLoadingCategorias] = useState(false);
    const [loadingProdutos, setLoadingProdutos] = useState(false);
    const [loadingServicos, setLoadingServicos] = useState(false);

    const [categoriaId, setCategoriaId] = useState('');
    const [produtoId, setProdutoId] = useState('');
    const [servicoId, setServicoId] = useState('');
    const [servicoSelecionado, setServicoSelecionado] = useState(null);

    useEffect(() => {
        const fetchCategorias = async () => {
            setLoadingCategorias(true);
            try {
                const data = await CatalogHierarchyService.getCategorias();
                setCategorias(data);
            } catch (err) {
                console.error('Erro ao buscar categorias:', err);
            } finally {
                setLoadingCategorias(false);
            }
        };
        fetchCategorias();
    }, []);

    const handleCategoriaChange = async (event) => {
        const catId = event.target.value;
        setCategoriaId(catId);
        setProdutoId('');
        setServicoId('');
        setServicoSelecionado(null);
        setProdutos([]);
        setServicos([]);

        if (!catId) return;

        setLoadingProdutos(true);
        try {
            const data = await CatalogHierarchyService.getProdutosByCategoria(catId);
            setProdutos(data);
        } catch (err) {
            console.error('Erro ao buscar produtos:', err);
        } finally {
            setLoadingProdutos(false);
        }
    };

    const handleProdutoChange = async (event) => {
        const prodId = event.target.value;
        setProdutoId(prodId);
        setServicoId('');
        setServicoSelecionado(null);
        setServicos([]);

        if (!prodId) return;

        setLoadingServicos(true);
        try {
            const data = await CatalogHierarchyService.getServicosByProduto(prodId);
            setServicos(data);
        } catch (err) {
            console.error('Erro ao buscar servicos:', err);
        } finally {
            setLoadingServicos(false);
        }
    };

    const handleServicoChange = (event) => {
        const srvId = event.target.value;
        setServicoId(srvId);
        
        const selected = servicos.find(s => String(s.id) === String(srvId));
        if (selected) {
            setServicoSelecionado({
                id: Date.now(), // Generate a unique ID for the ad-hoc equipment
                servico_id: selected.id,
                nome: selected.nome, // Preenche ambos para compatibilidade
                marca: '',
                modelo: '',
                serie: '',
                categoria_id: categoriaId,
                produto_id: produtoId
            });
        } else {
            setServicoSelecionado(null);
        }
    };

    const handleAdd = () => {
        if (servicoSelecionado) {
            onSelectEquipment(servicoSelecionado);
            // Reset for the next one
            setCategoriaId('');
            setProdutoId('');
            setServicoId('');
            setServicoSelecionado(null);
            setProdutos([]);
            setServicos([]);
        }
    };

    return (
        <Box className={classes.container}>
            <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr 1fr' }} gap={2}>
                <FormControl variant="outlined" size="small" className={classes.formControl}>
                    <InputLabel id="quote-ws-cat-label">Categoria</InputLabel>
                    <Select
                        labelId="quote-ws-cat-label"
                        value={categoriaId}
                        onChange={handleCategoriaChange}
                        label="Categoria"
                        disabled={loadingCategorias}
                    >
                        <MenuItem value=""><em>{loadingCategorias ? 'Carregando...' : 'Selecione a categoria'}</em></MenuItem>
                        {categorias.map(cat => (
                            <MenuItem key={cat.id} value={cat.id}>{cat.nome}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl variant="outlined" size="small" className={classes.formControl}>
                    <InputLabel id="quote-ws-prod-label">Produto/Família</InputLabel>
                    <Select
                        labelId="quote-ws-prod-label"
                        value={produtoId}
                        onChange={handleProdutoChange}
                        label="Produto/Família"
                        disabled={!categoriaId || loadingProdutos}
                    >
                        <MenuItem value=""><em>{!categoriaId ? 'Selecione a categoria' : loadingProdutos ? 'Carregando...' : 'Selecione o produto'}</em></MenuItem>
                        {produtos.map(prod => (
                            <MenuItem key={prod.id} value={prod.id}>{prod.nome}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl variant="outlined" size="small" className={classes.formControl}>
                    <InputLabel id="quote-ws-srv-label">Serviço/Equipamento</InputLabel>
                    <Select
                        labelId="quote-ws-srv-label"
                        value={servicoId}
                        onChange={handleServicoChange}
                        label="Serviço/Equipamento"
                        disabled={!produtoId || loadingServicos}
                    >
                        <MenuItem value=""><em>{!produtoId ? 'Selecione o produto' : loadingServicos ? 'Carregando...' : 'Selecione o serviço'}</em></MenuItem>
                        {servicos.map(srv => (
                            <MenuItem key={srv.id} value={srv.id}>{srv.nome}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            <Box className={classes.actionContainer}>
                <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={handleAdd}
                    disabled={!servicoSelecionado}
                    startIcon={<AddIcon />}
                    disableElevation
                >
                    Adicionar à Cotação
                </Button>
            </Box>
        </Box>
    );
};

export default withStyles(styles)(QuotePageEquipmentSearch);
